import { MAX_ITERATIONS, SMOOTHING_STEPS } from './fractalIteration';
import { PALETTE_SIZE, paletteTable } from './fractalPalette';

// GPU rendering shares the iteration budget and palette table with the CPU.
const VERTEX_SHADER_SRC = `
  precision highp float;
  attribute vec2 a_position;
  varying highp vec2 v_uv;
  void main() {
    v_uv = 0.5 * (a_position + 1.0);
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const FRAGMENT_SHADER_SRC = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform vec2 u_xRange;
  uniform vec2 u_yRange;
  uniform sampler2D u_palette;
  uniform float u_lagoon;
  uniform float u_gScale;
  uniform float u_phaseOffset;
  uniform float u_repeat;
  uniform float u_aScale;
  uniform float u_useMu;
  uniform float u_useFixedAlpha;
  uniform float u_aFixed;

  #ifdef DEEP_ZOOM
  invariant gl_FragColor;
  // A double-single number stores a leading float and its rounding residual.
  // Keep the compensated operations explicit: every intermediate is rounded
  // to highp precision. Coordinates AND the orbit need this extra precision.
  uniform vec2 u_centerX;
  uniform vec2 u_centerY;
  uniform vec2 u_spanX;
  uniform vec2 u_spanY;
  uniform vec2 u_resolution;
  // A runtime clamp preserves individual rounding points on fast-math drivers.
  // Its bound is far outside every value used by the bounded escape orbit.
  uniform float u_arithmeticBound;

  float addRounded(float a, float b) { return clamp(a + b, -u_arithmeticBound, u_arithmeticBound); }
  float subRounded(float a, float b) { return clamp(a - b, -u_arithmeticBound, u_arithmeticBound); }
  float mulRounded(float a, float b) { return clamp(a * b, -u_arithmeticBound, u_arithmeticBound); }

  vec2 dsAdd(vec2 a, vec2 b) {
    float s = addRounded(a.x, b.x);
    float v = subRounded(s, a.x);
    float e = addRounded(subRounded(a.x, subRounded(s, v)), subRounded(b.x, v));
    e = addRounded(addRounded(e, a.y), b.y);
    float hi = addRounded(s, e);
    return vec2(hi, subRounded(e, subRounded(hi, s)));
  }

  vec2 dsMul(vec2 a, vec2 b) {
    // Dekker split for a 24-bit significand (2^12 + 1).
    float ca = mulRounded(4097.0, a.x);
    float ah = subRounded(ca, subRounded(ca, a.x));
    float al = subRounded(a.x, ah);
    float cb = mulRounded(4097.0, b.x);
    float bh = subRounded(cb, subRounded(cb, b.x));
    float bl = subRounded(b.x, bh);
    float p = mulRounded(a.x, b.x);
    float e = addRounded(addRounded(addRounded(subRounded(mulRounded(ah, bh), p),
      mulRounded(ah, bl)), mulRounded(al, bh)), mulRounded(al, bl));
    e = addRounded(addRounded(e, mulRounded(a.x, b.y)), mulRounded(a.y, b.x));
    float hi = addRounded(p, e);
    return vec2(hi, subRounded(e, subRounded(hi, p)));
  }
  #endif

  void main() {
    #ifdef DEEP_ZOOM
    // Form a small offset before adding the split center. Interpolating the
    // absolute range endpoints would discard the low bits before iterating.
    vec2 offset = gl_FragCoord.xy / u_resolution - 0.5;
    vec2 cx = dsAdd(u_centerX, dsMul(u_spanX, vec2(offset.x, 0.0)));
    vec2 cy = dsAdd(u_centerY, dsMul(u_spanY, vec2(-offset.y, 0.0)));
    float x0 = cx.x + cx.y;
    float y0 = cy.x + cy.y;
    #else
    float x0 = mix(u_xRange.x, u_xRange.y, v_uv.x);
    // Flip Y to match the 2D canvas orientation (top row = yRange.x)
    float y0 = mix(u_yRange.y, u_yRange.x, v_uv.y);

    float q = (x0 - 0.25) * (x0 - 0.25) + y0 * y0;
    if (q * (q + x0 - 0.25) <= 0.25 * y0 * y0 ||
        (x0 + 1.0) * (x0 + 1.0) + y0 * y0 <= 0.0625) {
      gl_FragColor = vec4(0.0);
      return;
    }
    #endif

    // Mandelbrot: z² + c; start with x = y = 0
    float x = 0.0;
    float y = 0.0;
    float x2 = 0.0;
    float y2 = 0.0;
    float distSq = 0.0;
    float iterCount = 0.0;
    bool escaped = false;

    #ifdef DEEP_ZOOM
    vec2 zx = vec2(0.0);
    vec2 zy = vec2(0.0);
    vec2 zx2 = vec2(0.0);
    vec2 zy2 = vec2(0.0);
    for (int i = 0; i < ${MAX_ITERATIONS}; i++) {
      vec2 nextX = dsAdd(dsAdd(zx2, -zy2), cx);
      zy = dsAdd(2.0 * dsMul(zx, zy), cy);
      zx = nextX;
      zx2 = dsMul(zx, zx);
      zy2 = dsMul(zy, zy);
      iterCount += 1.0;
      // Check after advancing so the last permitted z_N is included.
      vec2 radius = dsAdd(zx2, zy2);
      if (radius.x > 4.0 || (radius.x == 4.0 && radius.y > 0.0)) {
        escaped = true;
        break;
      }
    }
    // Once escaped, ordinary floats suffice for the smooth color calculation.
    x = zx.x + zx.y;
    y = zy.x + zy.y;
    x2 = x * x;
    y2 = y * y;
    #else
    for (int i = 0; i < ${MAX_ITERATIONS}; i++) {
      float xnew = x2 - y2 + x0;
      float ynew = 2.0 * x * y + y0;
      x = xnew;
      y = ynew;
      x2 = x * x;
      y2 = y * y;
      iterCount += 1.0;
      distSq = x2 + y2;
      if (distSq > 4.0) {
        escaped = true;
        break;
      }
    }
    #endif

    // If we never escaped within MAX_ITERATIONS, leave pixel transparent
    if (!escaped) {
      gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
      return;
    }

    // Evaluate the continuous count farther along the escaped orbit to remove
    // the residual seams caused by smoothing at radius 2 alone.
    for (int i = 0; i < ${SMOOTHING_STEPS}; i++) {
      float xnew = x2 - y2 + x0;
      y = 2.0 * x * y + y0;
      x = xnew;
      x2 = x * x;
      y2 = y * y;
    }
    float logDist = 0.5 * log(x2 + y2);
    float mu = iterCount + ${SMOOTHING_STEPS + 1}.0 - log(logDist) / log(2.0);

    float val = u_useMu > 0.5 ? mu : iterCount;
    float position = val * u_gScale / 255.0;
    float t = u_repeat > 0.5
      ? 0.5 - 0.5 * cos(3.141592653589793 * position)
      : clamp(position, 0.0, 1.0);
    float a = u_useFixedAlpha > 0.5 ? u_aFixed : clamp(val * u_aScale, 0.0, 255.0);
    vec3 color;
    if (u_lagoon > 0.5) {
      float phase = (sqrt(max(0.0, val) + 1.0) - 1.0) * u_gScale / 55.0 + u_phaseOffset;
      // Half a texel places integral table positions at sample centers.
      color = texture2D(u_palette, vec2(fract(phase) + 0.5 / ${PALETTE_SIZE}.0, 0.5)).rgb;
    } else {
      color = mix(vec3(0.0, 0.0, 1.0), vec3(1.0, 1.0, 0.0), t);
    }
    gl_FragColor = vec4(color, a / 255.0);
  }
`;

export function splitFloat(value) {
  const high = Math.fround(value);
  return [high, Math.fround(value - high)];
}

// Switch before coordinate rounding becomes visible. Keep extra headroom for
// accumulated orbit error, and use hysteresis so small zoom changes don't
// alternate shaders. The arithmetic floor accounts for orbits near |z| = 1.
export function needsDeepZoom(xRange, yRange, width, height, wasDeep = false, precision = 23) {
  const unitsPerPixel = Math.min(
    Math.abs(xRange[1] - xRange[0]) / width,
    Math.abs(yRange[1] - yRange[0]) / height
  );
  const magnitude = Math.max(1, ...xRange.map(Math.abs), ...yRange.map(Math.abs));
  const roundingStep = 2 ** (Math.floor(Math.log2(magnitude)) - precision);
  return unitsPerPixel <= roundingStep * (wasDeep ? 32 : 16);
}

function compileShader(gl, type, src, deferValidation = false) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, src);
  gl.compileShader(shader);
  if (!deferValidation && !gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`Shader compile: ${log}`);
  }
  return shader;
}

function createProgram(gl, vsSrc, fsSrc, deferValidation = false) {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc, deferValidation);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc, deferValidation);
  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!deferValidation && !gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`Program link: ${log}`);
  }
  return program;
}

function deepProgram(gl) {
  return createProgram(gl, VERTEX_SHADER_SRC, '#define DEEP_ZOOM\n' + FRAGMENT_SHADER_SRC, true);
}

/**
 * Try to get a WebGL context. Prefer WebGL2, fall back to WebGL1.
 * Returns { gl, isWebGL2 } or null if not supported.
 */
export function getWebGLContext(canvas) {
  if (!canvas) return null;
  let gl = typeof WebGL2RenderingContext !== 'undefined'
    ? canvas.getContext('webgl2', { alpha: false, antialias: false }) : null;
  const isWebGL2 = !!gl;
  if (!gl) {
    gl = canvas.getContext('webgl', { alpha: false, antialias: false });
  }
  if (!gl) return null;
  return { gl, isWebGL2 };
}

const programBindings = new WeakMap();
const QUAD_VERTICES = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
const UNIFORM_NAMES = [
  'u_xRange', 'u_yRange', 'u_gScale', 'u_aScale', 'u_useMu',
  'u_centerX', 'u_centerY', 'u_spanX', 'u_spanY', 'u_resolution', 'u_arithmeticBound',
  'u_phaseOffset', 'u_repeat', 'u_useFixedAlpha', 'u_aFixed', 'u_palette', 'u_lagoon'
];

function getProgramBindings(gl, program) {
  let bindings = programBindings.get(program);
  if (!bindings) {
    bindings = { a_position: gl.getAttribLocation(program, 'a_position') };
    for (const name of UNIFORM_NAMES) bindings[name] = gl.getUniformLocation(program, name);
    programBindings.set(program, bindings);
  }
  return bindings;
}

// The cached image uses framebuffer coordinates (bottom-left origin). During a
// drag, snap the camera to this pixel grid; the gesture's final draw is exact.
export function planWebGLPan(previous, xRange, yRange, width, height) {
  if (!previous || previous.width !== width || previous.height !== height) return null;
  const sx = (previous.xRange[1] - previous.xRange[0]) / width;
  const sy = (previous.yRange[1] - previous.yRange[0]) / height;
  if (!Number.isFinite(sx) || !Number.isFinite(sy) || sx === 0 || sy === 0) return null;
  // Express the tolerance in pixels, not absolute world coordinates: an
  // absolute epsilon can accidentally accept a zoom as a pan at deep scales.
  if (Math.abs((xRange[1] - xRange[0]) / sx - width) > 1e-4 ||
      Math.abs((yRange[1] - yRange[0]) / sy - height) > 1e-4) return null;
  const dx = Math.round((xRange[0] - previous.xRange[0]) / sx);
  const dy = Math.round((yRange[0] - previous.yRange[0]) / sy);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || Math.abs(dx) >= width || Math.abs(dy) >= height) return null;
  const strips = [];
  if (dx) strips.push([dx > 0 ? width - dx : 0, 0, Math.abs(dx), height]);
  // Exclude the corner already covered by the vertical strip.
  if (dy) strips.push([dx < 0 ? -dx : 0, dy > 0 ? 0 : height + dy,
    width - Math.abs(dx), Math.abs(dy)]);
  return { dx, dy, strips,
    xRange: previous.xRange.map(x => x + dx * sx),
    yRange: previous.yRange.map(y => y + dy * sy),
    shadedPixels: strips.reduce((sum, [, , w, h]) => sum + w * h, 0) };
}

const COPY_FRAGMENT_SHADER = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform sampler2D u_image;
  uniform vec2 u_offset;
  void main() { gl_FragColor = texture2D(u_image, v_uv + u_offset); }
`;

const ZOOM_FRAGMENT_SHADER = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform sampler2D u_source;
  uniform sampler2D u_detail;
  // Source texture coordinates for the requested camera's bottom-left UV.
  uniform vec4 u_projection;
  uniform float u_blend;
  void main() {
    if (u_blend >= 1.0) {
      gl_FragColor = texture2D(u_detail, v_uv);
      return;
    }
    vec2 sourceUV = v_uv * u_projection.xz + u_projection.yw;
    bool covered = all(greaterThanEqual(sourceUV, vec2(0.0))) &&
      all(lessThanEqual(sourceUV, vec2(1.0)));
    vec4 oldColor = covered ? texture2D(u_source, sourceUV)
      : vec4(10.0 / 255.0, 14.0 / 255.0, 24.0 / 255.0, 1.0);
    if (u_blend <= 0.0) {
      gl_FragColor = oldColor;
      return;
    }
    vec4 detailColor = texture2D(u_detail, v_uv);
    gl_FragColor = mix(oldColor, detailColor, u_blend);
  }
`;

// Map the requested viewport's bottom-left texture coordinates back to the
// last exact image. The Y offset differs from X because yRange is top-first.
export function planWebGLZoom(previous, xRange, yRange, width, height) {
  if (!previous || previous.width !== width || previous.height !== height) return null;
  const oldX = previous.xRange[1] - previous.xRange[0];
  const oldY = previous.yRange[1] - previous.yRange[0];
  const newX = xRange[1] - xRange[0];
  const newY = yRange[1] - yRange[0];
  if (![oldX, oldY, newX, newY].every(value => Number.isFinite(value) && value > 0)) return null;
  const projection = [newX / oldX, (xRange[0] - previous.xRange[0]) / oldX,
    newY / oldY, (previous.yRange[1] - yRange[1]) / oldY];
  return projection.every(Number.isFinite) ? projection : null;
}

function bindQuad(gl, canvas, position) {
  let buffer = canvas._fractalWebGLBuffer;
  if (!buffer) {
    buffer = gl.createBuffer();
    canvas._fractalWebGLBuffer = buffer;
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTICES, gl.STATIC_DRAW);
  } else gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
}

function copyPanFrame(gl, canvas, cache, pan) {
  if (!cache.program) {
    cache.program = createProgram(gl, VERTEX_SHADER_SRC, COPY_FRAGMENT_SHADER);
    cache.position = gl.getAttribLocation(cache.program, 'a_position');
    cache.image = gl.getUniformLocation(cache.program, 'u_image');
    cache.offset = gl.getUniformLocation(cache.program, 'u_offset');
  }
  gl.useProgram(cache.program);
  gl.disable(gl.BLEND);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.texture);
  gl.uniform1i(cache.image, 0);
  gl.uniform2f(cache.offset, pan.dx / canvas.width, -pan.dy / canvas.height);
  bindQuad(gl, canvas, cache.position);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

function savePanFrame(gl, canvas, frame) {
  let cache = canvas._fractalPanCache;
  if (!cache) cache = canvas._fractalPanCache = { texture: gl.createTexture() };
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.texture);
  if (cache.width !== frame.width || cache.height !== frame.height) {
    // The drawing buffer is opaque (alpha:false). WebGL 1 requires a compatible
    // RGB destination for copying it; sampling RGB supplies an alpha of one.
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, frame.width, frame.height, 0, gl.RGB, gl.UNSIGNED_BYTE, null);
    cache.width = frame.width;
    cache.height = frame.height;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  // Capture before the browser discards the default drawing buffer. This is a
  // GPU-to-GPU copy; there is no readPixels/readback or preserveDrawingBuffer.
  gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, frame.width, frame.height);
  cache.frame = frame;
}

export function disposeWebGLPanCache(canvas) {
  const cache = canvas?._fractalPanCache;
  if (!cache) return;
  const gl = getWebGLContext(canvas)?.gl;
  if (gl) {
    gl.deleteTexture(cache.texture);
    if (cache.program) gl.deleteProgram(cache.program);
  }
  delete canvas._fractalPanCache;
}

function releaseZoomResources(gl, cache) {
  if (cache.sourceTexture) gl.deleteTexture(cache.sourceTexture);
  if (cache.detailTexture) gl.deleteTexture(cache.detailTexture);
  if (cache.framebuffer) gl.deleteFramebuffer(cache.framebuffer);
  if (cache.program) gl.deleteProgram(cache.program);
  cache.sourceTexture = null;
  cache.detailTexture = null;
  cache.framebuffer = null;
  cache.program = null;
  cache.frame = null;
  cache.previousFrame = null;
  cache.transition = null;
  cache.interactive = false;
  cache.panPreview = false;
}

export function disposeWebGLZoomCache(canvas) {
  const cache = canvas?._fractalZoomCache;
  if (!cache) return;
  releaseZoomResources(cache.gl, cache);
  delete canvas._fractalZoomCache;
}

// Allocate the source when an exact visible frame is drawn. The composite
// program and second full-size target are deferred until somebody zooms.
function prepareZoomCache(gl, canvas, width, height, { program = false, detail = false } = {}) {
  let cache = canvas._fractalZoomCache;
  if (cache?.failed) return null;
  if (!cache) cache = canvas._fractalZoomCache = { gl };
  try {
    if (!cache.sourceTexture) {
      cache.sourceTexture = gl.createTexture();
      if (!cache.sourceTexture) throw new Error('Zoom source unavailable');
    }
    if (program && !cache.program) {
      cache.program = createProgram(gl, VERTEX_SHADER_SRC, ZOOM_FRAGMENT_SHADER);
      cache.position = gl.getAttribLocation(cache.program, 'a_position');
      cache.source = gl.getUniformLocation(cache.program, 'u_source');
      cache.detail = gl.getUniformLocation(cache.program, 'u_detail');
      cache.projection = gl.getUniformLocation(cache.program, 'u_projection');
      cache.blend = gl.getUniformLocation(cache.program, 'u_blend');
    }
    const resized = cache.width !== width || cache.height !== height;
    if (resized) {
      cache.width = width;
      cache.height = height;
      cache.frame = null;
      cache.previousFrame = null;
      cache.transition = null;
      cache.interactive = false;
      cache.panPreview = false;
    }
    const newDetail = detail && !cache.detailTexture;
    if (newDetail) {
      cache.detailTexture = gl.createTexture();
      cache.framebuffer = gl.createFramebuffer();
      if (!cache.detailTexture || !cache.framebuffer) throw new Error('Zoom target unavailable');
    }
    for (const texture of [resized && cache.sourceTexture,
      cache.detailTexture && (resized || newDetail) && cache.detailTexture].filter(Boolean)) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      // RGB is renderable in WebGL1 and matches the opaque drawing buffer.
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, width, height,
        0, gl.RGB, gl.UNSIGNED_BYTE, null);
    }
    if (detail) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, cache.framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, cache.detailTexture, 0);
      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error('Zoom target incomplete');
      }
    }
    return cache;
  } catch (error) {
    // Caching is optional: resource pressure must not disable exact WebGL draws.
    releaseZoomResources(gl, cache);
    cache.failed = true;
    return null;
  } finally {
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
}

function presentZoomFrame(gl, canvas, cache, projection, blend = 0) {
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, canvas.width, canvas.height);
  gl.useProgram(cache.program);
  gl.disable(gl.BLEND);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
  gl.uniform1i(cache.source, 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, cache.transition ? cache.detailTexture : cache.sourceTexture);
  gl.uniform1i(cache.detail, 1);
  gl.uniform4f(cache.projection, ...projection);
  gl.uniform1f(cache.blend, blend);
  bindQuad(gl, canvas, cache.position);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
}

function captureZoomFrame(gl, canvas, frame) {
  const cache = prepareZoomCache(gl, canvas, frame.width, frame.height);
  if (!cache) return;
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
  gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, frame.width, frame.height);
  cache.frame = frame;
  cache.transition = null;
  cache.interactive = false;
  cache.panPreview = false;
}

/** Present one blend frame. At progress 1, promote the new exact texture. */
export function presentWebGLZoomTransition(canvas, progress) {
  const cache = canvas?._fractalZoomCache;
  if (!cache?.transition || cache.gl.isContextLost?.()) return false;
  if (cache.transition.frame.width !== canvas.width || cache.transition.frame.height !== canvas.height) {
    discardWebGLZoomTransition(canvas);
    return false;
  }
  const amount = Math.min(1, Math.max(0, progress));
  presentZoomFrame(cache.gl, canvas, cache, cache.transition.projection, amount);
  cache.transition.displayedBlend = amount;
  if (amount === 1) {
    const oldFrame = cache.frame;
    [cache.sourceTexture, cache.detailTexture] = [cache.detailTexture, cache.sourceTexture];
    cache.frame = cache.transition.frame;
    cache.previousFrame = oldFrame?.exact === false ? null : oldFrame;
    cache.transition = null;
    cache.interactive = false;
  }
  return true;
}

/** Retain the visible fade at its target camera before another gesture starts. */
export function retainWebGLZoomTransition(canvas) {
  const cache = canvas?._fractalZoomCache;
  const transition = cache?.transition;
  if (!transition || cache.gl.isContextLost?.() ||
      transition.frame.width !== canvas.width || transition.frame.height !== canvas.height) return false;
  try {
    const gl = cache.gl;
    // The default drawing buffer may have been discarded after presentation.
    // Recreate its last blend from the two retained textures before copying.
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.DEPTH_TEST);
    presentZoomFrame(gl, canvas, cache, transition.projection, transition.displayedBlend ?? 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, cache.sourceTexture);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, canvas.width, canvas.height);
    cache.frame = { ...transition.frame, exact: false };
    cache.previousFrame = null;
    cache.transition = null;
    cache.interactive = true;
    return true;
  } catch (error) {
    // The next exact render remains available if texture retention fails.
    return false;
  }
}

/** Drop pending detail without changing the retained exact source image. */
export function discardWebGLZoomTransition(canvas) {
  const cache = canvas?._fractalZoomCache;
  if (!cache?.transition) return false;
  cache.transition = null;
  cache.interactive = false;
  return true;
}

/** Restore the cached exact bitmap for CSS transforms during deep handoff. */
export function presentWebGLZoomSource(canvas) {
  let cache = canvas?._fractalZoomCache;
  if (!cache?.frame || cache.gl.isContextLost?.()) return false;
  if (!cache.transition && !cache.interactive && !cache.panPreview) return false;
  if (cache.transition) presentWebGLZoomTransition(canvas, 1);
  else {
    cache = prepareZoomCache(cache.gl, canvas, canvas.width, canvas.height, { program: true });
    if (!cache?.frame) return false;
    presentZoomFrame(cache.gl, canvas, cache, [1, 0, 1, 0]);
  }
  cache.interactive = false;
  cache.panPreview = false;
  return true;
}

/**
 * Draw the Mandelbrot set with WebGL.
 * colorParams: { r, gScale, b, aScale, aFixed, useMu } (same as worker).
 * options.cachePan retains a GPU image; options.pan reuses compatible pixels
 * at a snapped viewport. options.interactiveZoom reprojects the last exact
 * full-resolution frame while the gesture is active. options.interactiveExact
 * computes exact pixels during a gesture and blends them from the projected
 * source before promoting the exact frame.
 * Omit options for an exact final/undo draw.
 * Returns true if drawn, false if WebGL failed (caller should fall back to canvas).
 */
export function drawMandelbrotFractalWebGL(canvasEl, xRange, yRange, colorParams, options = {}) {
  const requestedXRange = xRange;
  const requestedYRange = yRange;
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;

  const { gl } = ctx;
  if (gl.isContextLost?.()) return false;
  const w = canvasEl.width;
  const h = canvasEl.height;

  if (w <= 0 || h <= 0) return false;

  if (canvasEl._fractalFloatPrecision === undefined) {
    canvasEl._fractalFloatPrecision = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT)?.precision ?? 23;
  }
  const deepZoom = needsDeepZoom(xRange, yRange, w, h,
    canvasEl._fractalDeepZoom, canvasEl._fractalFloatPrecision);
  const paletteKey = JSON.stringify(colorParams || {});
  const cached = canvasEl._fractalPanCache?.frame;
  let pan = !options.interactiveZoom && !options.interactiveExact && options.cachePan && options.pan && !canvasEl._fractalPanCache?.failed &&
    cached?.deepZoom === deepZoom && cached.paletteKey === paletteKey
    ? planWebGLPan(cached, xRange, yRange, w, h) : null;
  if (pan) {
    xRange = pan.xRange;
    yRange = pan.yRange;
  }
  // Start compiling shortly before the switch. With parallel compilation,
  // the current shader keeps rendering while the driver prepares this one.
  if (!deepZoom && !canvasEl._fractalDeepWebGLProgram &&
      !canvasEl._fractalPendingDeepProgram &&
      needsDeepZoom(xRange, yRange, w * 4, h * 4, false, canvasEl._fractalFloatPrecision)) {
    if (gl.getExtension('KHR_parallel_shader_compile')) {
      canvasEl._fractalPendingDeepProgram = deepProgram(gl);
    }
  }
  const zoomCache = canvasEl._fractalZoomCache;
  if (zoomCache?.transition) presentWebGLZoomTransition(canvasEl, 1);
  const source = zoomCache?.frame;
  const projection = source?.paletteKey === paletteKey
    ? planWebGLZoom(source, xRange, yRange, w, h) : null;
  const prior = zoomCache?.previousFrame;
  const replay = options.twoFrameCache && !deepZoom && !options.interactiveZoom &&
    !options.interactiveExact && !options.cachePan && zoomCache?.program &&
    zoomCache.sourceTexture && zoomCache.detailTexture && prior &&
    !zoomCache.interactive && !zoomCache.panPreview && !zoomCache.transition &&
    source?.exact !== false && prior.exact !== false &&
    source.deepZoom === false && prior.deepZoom === false &&
    source.paletteKey === paletteKey && prior.paletteKey === paletteKey &&
    source.width === w && source.height === h && prior.width === w && prior.height === h &&
    prior.xRange[0] === xRange[0] && prior.xRange[1] === xRange[1] &&
    prior.yRange[0] === yRange[0] && prior.yRange[1] === yRange[1];
  if (replay) {
    const oldFrame = zoomCache.frame;
    const replayTexture = zoomCache.detailTexture;
    [zoomCache.sourceTexture, zoomCache.detailTexture] =
      [zoomCache.detailTexture, zoomCache.sourceTexture];
    zoomCache.frame = prior;
    zoomCache.previousFrame = oldFrame;
    let presented = false;
    let restoreFailed = false;
    try {
      gl.disable(gl.SCISSOR_TEST);
      gl.disable(gl.DEPTH_TEST);
      // Exact 1:1 history pixels must sample their original texel centers.
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, replayTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      presentZoomFrame(gl, canvasEl, zoomCache, [1, 0, 1, 0]);
      if (gl.getError?.() !== gl.NO_ERROR) throw new Error('WebGL history replay failed');
      presented = true;
    } catch (error) {
      presented = false;
    } finally {
      try {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, replayTexture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        if (gl.getError?.() !== gl.NO_ERROR) restoreFailed = true;
      } catch (error) { restoreFailed = true; }
    }
    if (restoreFailed) {
      // A texture with an unknown filter must never be used by a later fade.
      releaseZoomResources(gl, zoomCache);
      zoomCache.failed = true;
      presented = false;
    } else if (!presented) {
      [zoomCache.sourceTexture, zoomCache.detailTexture] =
        [zoomCache.detailTexture, zoomCache.sourceTexture];
      zoomCache.frame = oldFrame;
      zoomCache.previousFrame = null;
    }
    if (presented) {
      canvasEl._fractalDeepZoom = false;
      if (canvasEl._fractalPanCache) canvasEl._fractalPanCache.frame = null;
      zoomCache.replayHits = (zoomCache.replayHits || 0) + 1;
      canvasEl._fractalRenderStats = { reused: true, twoFrameReplay: true,
        shadedPixels: 0, totalPixels: w * h };
      return true;
    }
  }
  const activeZoomCache = options.interactiveZoom && !options.interactiveExact && projection
    ? prepareZoomCache(gl, canvasEl, w, h, { program: true }) : null;
  if (activeZoomCache && projection) {
    canvasEl._fractalDeepZoom = deepZoom;
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.DEPTH_TEST);
    presentZoomFrame(gl, canvasEl, activeZoomCache, projection);
    activeZoomCache.interactive = true;
    if (canvasEl._fractalPanCache) canvasEl._fractalPanCache.frame = null;
    canvasEl._fractalRenderStats = { reused: true, reprojected: true, interactiveZoom: true,
      shadedPixels: 0, totalPixels: w * h };
    return true;
  }
  if (zoomCache) zoomCache.previousFrame = null;
  const programKey = deepZoom ? '_fractalDeepWebGLProgram' : '_fractalWebGLProgram';
  let program = canvasEl[programKey];
  if (!program) {
    try {
      if (deepZoom && canvasEl._fractalPendingDeepProgram) {
        program = canvasEl._fractalPendingDeepProgram;
        canvasEl._fractalPendingDeepProgram = null;
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
          const log = gl.getProgramInfoLog(program);
          gl.deleteProgram(program);
          throw new Error(`Deep zoom shader link: ${log}`);
        }
      } else {
        program = createProgram(gl, VERTEX_SHADER_SRC,
          (deepZoom ? '#define DEEP_ZOOM\n' : '') + FRAGMENT_SHADER_SRC);
      }
      canvasEl[programKey] = program;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('WebGL program creation failed:', err.message);
      }
      return false;
    }
  }
  canvasEl._fractalDeepZoom = deepZoom;

  const cp = colorParams || {};
  const gScale = cp.gScale !== undefined ? cp.gScale : 7;
  const aScale = cp.aScale !== undefined ? cp.aScale : 15;
  const useMu = cp.useMu !== undefined ? cp.useMu : true;

  const sameSourceCamera = source && source.xRange[0] === xRange[0] && source.xRange[1] === xRange[1] &&
    source.yRange[0] === yRange[0] && source.yRange[1] === yRange[1];
  const transitionCache = !options.interactiveZoom && !options.cachePan && projection &&
    (options.interactiveExact && !sameSourceCamera || !options.interactiveExact && zoomCache?.interactive)
    ? prepareZoomCache(gl, canvasEl, w, h, { program: true, detail: true }) : null;
  if (transitionCache) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, transitionCache.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D,
      transitionCache.detailTexture, 0);
  } else gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, w, h);
  gl.disable(gl.SCISSOR_TEST);
  gl.disable(gl.DEPTH_TEST);
  if (pan) {
    try {
      copyPanFrame(gl, canvasEl, canvasEl._fractalPanCache, pan);
    } catch (error) {
      // A copy-shader failure must not take down the working fractal renderer.
      canvasEl._fractalPanCache.failed = true;
      pan = null;
      xRange = requestedXRange;
      yRange = requestedYRange;
    }
  }
  gl.useProgram(program);

  // The opaque WebGL drawing buffer must use the same background as CSS/2D.
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
  gl.clearColor(10 / 255, 14 / 255, 24 / 255, 1);
  if (!pan) gl.clear(gl.COLOR_BUFFER_BIT);

  const bindings = getProgramBindings(gl, program);
  const { u_xRange, u_yRange, u_gScale, u_aScale, u_useMu } = bindings;

  gl.uniform2f(u_xRange, xRange[0], xRange[1]);
  gl.uniform2f(u_yRange, yRange[0], yRange[1]);
  if (deepZoom) {
    const spanX = xRange[1] - xRange[0];
    const spanY = yRange[1] - yRange[0];
    gl.uniform2f(bindings.u_centerX, ...splitFloat(xRange[0] + spanX / 2));
    gl.uniform2f(bindings.u_centerY, ...splitFloat(yRange[0] + spanY / 2));
    gl.uniform2f(bindings.u_spanX, ...splitFloat(spanX));
    gl.uniform2f(bindings.u_spanY, ...splitFloat(spanY));
    gl.uniform2f(bindings.u_resolution, w, h);
    gl.uniform1f(bindings.u_arithmeticBound, 1e30);
  }
  gl.uniform1f(u_gScale, gScale);
  gl.uniform1f(bindings.u_phaseOffset, cp.phaseOffset ?? 0);
  gl.uniform1f(bindings.u_repeat, cp.repeat ? 1 : 0);
  const useFixedAlpha = cp.aFixed != null && cp.aFixed !== undefined;
  gl.uniform1f(u_aScale, aScale);
  gl.uniform1f(u_useMu, useMu ? 1 : 0);
  gl.uniform1f(bindings.u_useFixedAlpha, useFixedAlpha ? 1 : 0);
  gl.uniform1f(bindings.u_aFixed, useFixedAlpha ? cp.aFixed : 0);

  gl.activeTexture(gl.TEXTURE0);
  let texture = canvasEl._fractalPaletteTexture;
  if (!texture) {
    texture = gl.createTexture();
    canvasEl._fractalPaletteTexture = texture;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, PALETTE_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, paletteTable);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  } else {
    gl.bindTexture(gl.TEXTURE_2D, texture);
  }
  gl.uniform1i(bindings.u_palette, 0);
  gl.uniform1f(bindings.u_lagoon, cp.style === 'lagoon' ? 1 : 0);

  bindQuad(gl, canvasEl, bindings.a_position);
  if (pan) {
    gl.enable(gl.SCISSOR_TEST);
    for (const strip of pan.strips) {
      gl.scissor(...strip);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.disable(gl.SCISSOR_TEST);
  } else gl.drawArrays(gl.TRIANGLES, 0, 6);
  const exactFrame = {
    xRange: [...xRange], yRange: [...yRange], width: w, height: h, deepZoom, paletteKey
  };
  if (transitionCache) {
    transitionCache.transition = { frame: exactFrame, projection,
      interactiveExact: !!options.interactiveExact };
    transitionCache.interactive = false;
    presentZoomFrame(gl, canvasEl, transitionCache, projection);
  } else if (options.cachePan && options.pan) {
    if (canvasEl._fractalZoomCache) canvasEl._fractalZoomCache.panPreview = true;
  } else captureZoomFrame(gl, canvasEl, exactFrame);
  if (!options.interactiveZoom && options.cachePan && !canvasEl._fractalPanCache?.failed) savePanFrame(gl, canvasEl, {
    xRange: [...xRange], yRange: [...yRange], width: w, height: h, deepZoom, paletteKey
  });
  else if (canvasEl._fractalPanCache) canvasEl._fractalPanCache.frame = null;
  // Exposed for the benchmark: counts fractal evaluations, excluding the cheap
  // texture presentation and copy, whose cost is included in measured time.
  canvasEl._fractalRenderStats = { reused: !!pan, shadedPixels: pan ? pan.shadedPixels : w * h,
    totalPixels: w * h };
  if (options.interactiveZoom) canvasEl._fractalRenderStats.interactiveZoom = true;
  if (options.interactiveExact) canvasEl._fractalRenderStats.interactiveExact = true;
  if (transitionCache) canvasEl._fractalRenderStats.transitionZoom = true;
  return true;
}

/**
 * Call once per canvas when choosing render path. If this returns true,
 * the canvas has a WebGL context and future draws should use drawMandelbrotFractalWebGL.
 * If false, use getContext('2d') and the canvas/worker path.
 */
export function tryWebGL(canvasEl) {
  const ctx = getWebGLContext(canvasEl);
  if (!ctx) return false;
  canvasEl._useWebGL = true;
  return true;
}

export function useWebGL(canvasEl) {
  return !!(canvasEl && canvasEl._useWebGL);
}
